/**
 * @file Organization data managed via TanStack Query.
 *
 * Provides query options and mutations for organization management.
 * Used by route loaders for URL-based organization context.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const organizationKeys = {
  all: ["organization"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  list: () => [...organizationKeys.lists()] as const,
  details: () => [...organizationKeys.all, "detail"] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  bySlug: (slug: string) => [...organizationKeys.all, "bySlug", slug] as const,
  members: (orgId: string) =>
    [...organizationKeys.all, orgId, "members"] as const,
  invitations: (orgId: string) =>
    [...organizationKeys.all, orgId, "invitations"] as const,
  invitation: (invitationId: string) =>
    [...organizationKeys.all, "invitation", invitationId] as const,
};

// Legacy exports for backwards compatibility
export const organizationBySlugQueryKey = (slug: string) =>
  organizationKeys.bySlug(slug);
export const organizationListQueryKey = organizationKeys.list();

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for fetching organization by slug.
 * Returns the full organization with members.
 */
export function organizationBySlugQueryOptions(slug: string) {
  return queryOptions({
    queryKey: organizationKeys.bySlug(slug),
    queryFn: () => trpcClient.organization.getBySlug.query({ slug }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry(failureCount, error) {
      const status = (error as { data?: { httpStatus?: number } })?.data
        ?.httpStatus;
      if (status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Query options for fetching organization by ID.
 * Returns full organization details with current user's role.
 */
export function organizationQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: organizationKeys.detail(organizationId),
    queryFn: () => trpcClient.organization.get.query({ organizationId }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for listing all organizations the user belongs to.
 */
export function organizationListQueryOptions() {
  return queryOptions({
    queryKey: organizationKeys.list(),
    queryFn: () => trpcClient.organization.list.query(),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for listing organizations contextual to a given org slug.
 *
 * Returns { contextOrgs, personalOrgs }:
 * - contextOrgs: the org's B2B network (org itself + managed/delegated accounts).
 * - personalOrgs: the calling user's own personal org memberships (for the submenu).
 */
export function organizationListForContextQueryOptions(orgSlug: string) {
  return queryOptions({
    queryKey: [...organizationKeys.list(), "context", orgSlug] as const,
    queryFn: () =>
      trpcClient.organization.listInContext.query({
        organizationSlug: orgSlug,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry(failureCount, error) {
      const status = (error as { data?: { httpStatus?: number } })?.data
        ?.httpStatus;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Query options for fetching organization members.
 */
export function organizationMembersQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: organizationKeys.members(organizationId),
    queryFn: () =>
      trpcClient.organization.members.list.query({ organizationId }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for fetching organization invitations.
 * Uses staleTime: 0 since invitations can be accepted/rejected by other users.
 */
export function organizationInvitationsQueryOptions(
  organizationId: string,
  status?: "pending" | "accepted" | "rejected" | "expired" | "canceled",
) {
  return queryOptions({
    queryKey: [...organizationKeys.invitations(organizationId), status],
    queryFn: () =>
      trpcClient.organization.invitations.list.query({
        organizationId,
        status,
      }),
    staleTime: 0,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for fetching a single invitation by ID.
 * Used by the invitation acceptance page.
 */
export function invitationQueryOptions(invitationId: string) {
  return queryOptions({
    queryKey: organizationKeys.invitation(invitationId),
    queryFn: () =>
      trpcClient.organization.invitations.get.query({ invitationId }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry(failureCount, error) {
      const status = (error as { data?: { httpStatus?: number } })?.data
        ?.httpStatus;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Query options for fetching invitations for the current user.
 */
export function userInvitationsQueryOptions(
  status?: "pending" | "accepted" | "rejected" | "expired" | "canceled",
) {
  return queryOptions({
    queryKey: [...organizationKeys.all, "user-invitations", status] as const,
    queryFn: () =>
      trpcClient.organization.invitations.listUser.query({ status }),
    staleTime: 0,
    gcTime: 5 * 60_000,
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching organization by ID.
 */
export function useOrganizationQuery(organizationId: string) {
  return useQuery(organizationQueryOptions(organizationId));
}

/**
 * Hook for listing all organizations.
 */
export function useOrganizationListQuery() {
  return useQuery(organizationListQueryOptions());
}

/**
 * Hook for fetching organization members.
 */
export function useOrganizationMembersQuery(organizationId: string) {
  return useQuery(organizationMembersQueryOptions(organizationId));
}

/**
 * Hook for fetching organization invitations.
 */
export function useOrganizationInvitationsQuery(
  organizationId: string,
  status?: "pending" | "accepted" | "rejected" | "expired" | "canceled",
) {
  return useQuery(organizationInvitationsQueryOptions(organizationId, status));
}

/**
 * Hook for fetching invitations for the current user.
 */
export function useUserInvitationsQuery(
  status?: "pending" | "accepted" | "rejected" | "expired" | "canceled",
) {
  return useQuery(userInvitationsQueryOptions(status));
}

/**
 * Suspense hook for fetching organization by slug.
 * Use in components that need org data.
 */
export function useSuspenseOrganizationBySlug(slug: string) {
  return useSuspenseQuery(organizationBySlugQueryOptions(slug));
}

/**
 * Suspense hook for listing all organizations.
 */
export function useSuspenseOrganizationList() {
  return useSuspenseQuery(organizationListQueryOptions());
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for creating a new organization.
 * Navigates to the new organization on success.
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: {
      name: string;
      slug?: string;
      logo?: string;
      metadata?: Record<string, unknown>;
    }) => trpcClient.organization.create.mutate(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      const slug = (data as { slug?: string })?.slug;
      if (slug) {
        navigate({ to: "/o/$orgSlug", params: { orgSlug: slug } });
      }
    },
  });
}

/**
 * Hook for updating an organization.
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      name?: string;
      slug?: string;
      logo?: string;
      metadata?: Record<string, unknown>;
    }) => trpcClient.organization.update.mutate(data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.organizationId),
      });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      // Also invalidate by slug if available
      const slug = (data as { slug?: string })?.slug || variables.slug;
      if (slug) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.bySlug(slug),
        });
      }
    },
  });
}

/**
 * Hook for deleting an organization.
 * Navigates to home on success.
 */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (organizationId: string) =>
      trpcClient.organization.delete.mutate({ organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      navigate({ to: "/", search: { account_deleted: true } });
    },
    onError: async (error) => {
      // Surface failures instead of failing silently (EPD-10302).
      const { toast } = await import("@repo/ui");
      toast.error("Failed to delete account", {
        description: error.message,
      });
    },
  });
}

// ============================================================================
// Member Mutation Hooks
// ============================================================================

/**
 * Hook for adding a member to an organization.
 */
export function useAddOrganizationMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      userId: string;
      role: "owner" | "admin" | "member";
    }) => trpcClient.organization.members.add.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.organizationId),
      });
    },
  });
}

/**
 * Hook for updating a member's role.
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      organizationId: string;
      memberId: string;
      role: "owner" | "admin" | "member";
      slug?: string;
    }) => {
      const { slug: _slug, ...data } = variables;
      return trpcClient.organization.members.updateRole.mutate(data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.organizationId),
      });
      if (variables.slug) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.bySlug(variables.slug),
        });
      }
    },
  });
}

/**
 * Hook for removing a member from an organization.
 */
export function useRemoveOrganizationMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organizationId: string; memberId: string }) =>
      trpcClient.organization.members.remove.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.members(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.organizationId),
      });
    },
  });
}

/**
 * Hook for leaving an organization.
 */
export function useLeaveOrganization() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (organizationId: string) =>
      trpcClient.organization.members.leave.mutate({ organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      navigate({ to: "/" });
    },
  });
}

// ============================================================================
// Invitation Mutation Hooks
// ============================================================================

/**
 * Hook for creating an invitation.
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      email: string;
      role: "admin" | "member";
      expiresIn?: number;
    }) => trpcClient.organization.invitations.create.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.invitations(variables.organizationId),
      });
    },
  });
}

/**
 * Hook for canceling an invitation.
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organizationId: string; invitationId: string }) =>
      trpcClient.organization.invitations.cancel.mutate(data),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.invitations(variables.organizationId),
      });
      // Show success toast
      const { toast } = await import("@repo/ui");
      toast.success("Invitation canceled successfully");
    },
    onError: async (error) => {
      // Show error toast
      const { toast } = await import("@repo/ui");
      toast.error(`Failed to cancel invitation: ${error.message}`);
      console.error("Cancel invitation error:", error);
    },
  });
}

/**
 * Hook for resending a canceled or rejected invitation.
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organizationId: string; invitationId: string }) =>
      trpcClient.organization.invitations.resend.mutate(data),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.invitations(variables.organizationId),
      });
      const { toast } = await import("@repo/ui");
      toast.success("Invitation resent successfully");
    },
    onError: async (error) => {
      const { toast } = await import("@repo/ui");
      toast.error(`Failed to resend invitation: ${error.message}`);
      console.error("Resend invitation error:", error);
    },
  });
}

/**
 * Hook for accepting an invitation.
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (invitationId: string) =>
      trpcClient.organization.invitations.accept.mutate({ invitationId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      // Navigate to the organization
      const result = data as { organizationSlug?: string };
      if (result.organizationSlug) {
        navigate({
          to: "/o/$orgSlug",
          params: { orgSlug: result.organizationSlug },
        });
      }
    },
  });
}

/**
 * Hook for rejecting an invitation.
 */
export function useRejectInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      trpcClient.organization.invitations.reject.mutate({ invitationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

// ============================================================================
// Cache Utilities
// ============================================================================

/**
 * Get cached organization data by slug.
 * Returns undefined if not cached.
 */
export function getCachedOrganizationBySlug(
  queryClient: QueryClient,
  slug: string,
) {
  return queryClient.getQueryData(organizationKeys.bySlug(slug));
}

/**
 * Prefetch organization by slug.
 * Useful in route loaders.
 */
export async function prefetchOrganizationBySlug(
  queryClient: QueryClient,
  slug: string,
) {
  return queryClient.fetchQuery(organizationBySlugQueryOptions(slug));
}

/**
 * Invalidate all organization queries.
 * Useful after major changes.
 */
export function invalidateOrganizationQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: organizationKeys.all });
}
